<script lang="ts">
import { type MediaFile, MediaUtils } from '@monogrid/vue-boost/cms'
import { templateRef } from '@vueuse/core'
import gsap from 'gsap'
import { VideoTexture } from 'three'
import { computed, defineComponent, inject, PropType, watch } from 'vue'

import { lenisInjectionKey } from '@/App.vue'
import { useGlobalReducedMotion } from '@/functions/useGlobalReducedMotion'
import { useMediaMesh } from '@/functions/useMediaMesh'

export default defineComponent({
  name: 'LayerVideo',
  props: {
    cover: { type: Boolean, default: false },
    data: { type: Object as PropType<MediaFile>, required: true },
    dithered: { type: Boolean, default: false },
    hover: { type: Number, default: 0 },
    opacity: { type: Number, default: 1 },
    order: { type: Number, default: 0 },
    revealMix: { type: Number, default: 0 },
    scale: { type: Number, default: 1 },
    squareSize: { type: Number, default: 3 },
    transparent: { type: Boolean, default: false },
    waveStrength: { type: Number, default: 1 },
    webgl: { type: Boolean, default: true },
    z: { type: Number, default: undefined }
  },
  setup (props, { attrs, slots, emit }) {
    const lenis = inject(lenisInjectionKey)
    const wrapper = templateRef<HTMLElement>('wrapper')
    const video = templateRef<HTMLVideoElement>('video')
    const reducedMotion = useGlobalReducedMotion()
    const webglEnabled = computed(() => !!lenis.instance.value && props.webgl && !reducedMotion.value)
    const url = computed(() => (`/media/${props.data.id}`))

    const style = computed(() => ({ opacity: webglEnabled.value ? 0 : 2 - props.scale, '--scale': gsap.utils.mapRange(1, 2, 1, 1.3, props.scale) }))

    const mediaSize = computed<[number, number]>(() => {
      const metadata = MediaUtils.getMetaData(props.data)
      return [metadata['video-width'], metadata['video-height']]
    })

    const { mesh } = useMediaMesh(wrapper, {
      cover: () => props.cover,
      dithered: () => props.dithered,
      enabled: webglEnabled,
      hover: () => props.hover,
      opacity: () => props.opacity,
      order: () => props.order,
      revealMix: () => props.revealMix,
      scale: () => props.scale,
      squareSize: () => props.squareSize,
      transparent: () => props.transparent,
      waveStrength: () => props.waveStrength,
      z: () => props.z,
      mediaSize
    })

    watch(video, (el) => {
      if (!el) return
      mesh.material.uniforms.tImg.value = new VideoTexture(video.value)
    })

    return {
      url,
      webglEnabled,
      style
    }
  }
})
</script>

<template>
  <div ref="wrapper" class="overflow-hidden">
    <video
      ref="video"
      class="layer-video w-full h-full object-cover"
      :class="{'opacity-0': webglEnabled}"
      autoplay loop muted playsinline
      :style="style"
    >
      <source :src="url" type="video/mp4">
    </video>
  </div>
</template>

<style lang="scss" scoped>
.layer-video {
  transform: scale(var(--scale));
}
</style>
